/** Listado de corridas y lanzamiento de una nueva sincronización. */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api/client";
import { useAsync } from "../api/useAsync";
import type { Config, SyncProgress } from "../api/types";
import { ErrorState, Loading } from "../components/States";
import { StatusBadge } from "../components/StatusBadge";
import { SyncProgressLine } from "../components/SyncProgressLine";
import { runLabel, runTone } from "../components/tones";
import { formatCompact, formatDateTime } from "../components/format";

/** Cada cuánto se pregunta por el avance. Una corrida dura entre siete y once
 *  minutos y tiene siete fases, así que cinco segundos basta para no perderse
 *  ninguna sin convertir la pantalla en un generador de tráfico. */
const CADA = 5000;

export function Runs({ config, onConfigStale }: { config: Config; onConfigStale: () => void }) {
  const { data, error, loading, reload } = useAsync(() => api.runs(50), []);
  const [starting, setStarting] = useState(false);
  const [message, setMessage] = useState<{ tone: "good" | "critical"; text: string }>();
  /* `undefined` es «todavía no he preguntado»; `null` es «pregunté y no hay
     ninguna». La distinción evita que la línea de avance parpadee al entrar. */
  const [progress, setProgress] = useState<SyncProgress | null>();
  const habia = useRef(false);

  /* Se pregunta por el avance mientras haya corrida, y una vez al entrar para
     saber si la hay. Cuando la que había desaparece, se recarga la tabla: la
     corrida recién publicada tiene que aparecer sola, sin que nadie pulse F5. */
  useEffect(() => {
    let vivo = true;

    async function mirar() {
      try {
        const { run, unknown } = await api.syncProgress();
        if (!vivo || unknown) return;
        if (habia.current && run === null) reload();
        habia.current = run !== null;
        setProgress(run);
      } catch {
        /* Un fallo al preguntar por el avance no es un fallo de la pantalla:
           se conserva lo último que se supo y se reintenta en cinco segundos. */
      }
    }

    void mirar();
    const reloj = setInterval(() => void mirar(), CADA);
    return () => {
      vivo = false;
      clearInterval(reloj);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Lo que de verdad bloquea el botón es la corrida en vuelo, que ahora se
     consulta en la base. `config.active_run` sale de la memoria del proceso de
     la API y se usa SOLO mientras el sondeo no ha contestado todavía: en cuanto
     contesta, manda la base, porque ese diccionario en memoria se queda pegado
     si el Job muere sin poder liberarlo.

     `!== undefined` y no `??`: `null` es una respuesta —«no hay corrida»— y
     `null ?? x` la habría descartado, dejando el botón bloqueado para siempre
     con un `active_run` viejo. */
  const preguntado = progress !== undefined;
  const enCurso = preguntado ? progress : config.active_run ? undefined : null;

  async function startSync() {
    setStarting(true);
    setMessage(undefined);
    try {
      // La clave de idempotencia se deriva del token CSRF, que es de un solo
      // uso: un doble clic reutiliza la misma clave y no lanza dos extracciones.
      const result = await api.startSync(config.csrf_token);
      setMessage({
        tone: "good",
        text:
          result.status === "duplicate"
            ? "Esa solicitud ya se había atendido; no se lanzó una segunda extracción."
            : `Sincronización lanzada: ${result.execution}`,
      });
      reload();
    } catch (err) {
      const detail = err instanceof ApiError ? err.message : String(err);
      setMessage({ tone: "critical", text: detail });
    } finally {
      // El token CSRF quedó consumido: hay que pedir uno nuevo antes de reintentar.
      onConfigStale();
      setStarting(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Corridas</h1>
          <p>
            {/* La frase decía «no hay cron ni Cloud Scheduler», y dejó de ser
                cierta al añadirse el horario. Lo que sigue siendo cierto —y es
                lo que importa aquí— es que el botón sincroniza al instante y que
                nunca corren dos a la vez. */}
            La sincronización es manual y bajo demanda. También puede programarse
            desde <Link to="/settings">Membrete y firmas</Link>. Solo se permite
            una corrida a la vez.
          </p>
        </div>
        <button
          className="primary"
          onClick={startSync}
          disabled={starting || enCurso !== null}
        >
          {starting ? "Lanzando…" : "Sincronizar ahora"}
        </button>
      </div>

      {progress && <SyncProgressLine run={progress} />}

      {/* Hay corrida según la API pero el sondeo todavía no ha contestado.
          Dura un instante y evita que el aviso aparezca en dos saltos. En
          cuanto el sondeo contesta este aviso desaparece, tanto si trae fase
          como si dice que no hay ninguna. */}
      {!preguntado && config.active_run && (
        <div className="card banner">
          <StatusBadge tone="warning">En curso</StatusBadge>
          <div className="banner-body">
            <strong>Hay una sincronización en curso</strong>
            <p className="mono small">{config.active_run}</p>
          </div>
        </div>
      )}

      {message && (
        <div className={`card banner ${message.tone === "good" ? "good" : "critical"}`}>
          <StatusBadge tone={message.tone}>
            {message.tone === "good" ? "Aceptada" : "Rechazada"}
          </StatusBadge>
          <div className="banner-body">
            <p>{message.text}</p>
          </div>
        </div>
      )}

      {/* `!data`: en una recarga ya hay tabla que enseñar, y sustituirla por
          «Cargando las corridas» la hacía desaparecer y volver en cada clic. */}
      {loading && !data && <Loading what="las corridas" />}
      {error && !data && <ErrorState error={error} />}

      {data && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Corrida</th>
                <th scope="col">Estado</th>
                <th scope="col">Inicio</th>
                <th scope="col">Fin</th>
                <th scope="col" className="num">
                  Filas
                </th>
                <th scope="col" className="num">
                  Validaciones
                </th>
                <th scope="col">Disparada por</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    Todavía no se ha ejecutado ninguna sincronización.
                  </td>
                </tr>
              )}
              {data.map((run) => {
                const total = run.validation_count ?? 0;
                const failed = run.failed_validations ?? 0;
                return (
                  <tr key={run.sync_run_id}>
                    <td>
                      <Link className="mono" to={`/runs/${run.sync_run_id}`}>
                        {run.sync_run_id.slice(0, 12)}
                      </Link>
                    </td>
                    <td>
                      <StatusBadge tone={runTone(run.status)}>{runLabel(run.status)}</StatusBadge>
                    </td>
                    <td>{formatDateTime(run.started_at)}</td>
                    <td>{formatDateTime(run.finished_at)}</td>
                    <td className="num">{formatCompact(run.total_rows ?? 0)}</td>
                    <td className="num">
                      {total - failed}/{total}
                    </td>
                    <td>{run.triggered_by ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
